import {
  APIMutationOptions,
  APIMutationResult,
  useAPIMutation,
} from 'shared/api/APIMutation';
import {
  APIQueryOptions,
  APIQueryResult,
  useAPIQuery,
  UseAPIQueryOptions,
} from 'shared/api/APIQuery';
import { APIResponse, requestCarrierAPI } from 'shared/api/CarrierAPIClient';
import {
  instantTransferStatisticsSchema,
  InstantTransferStatisticsSchemaDTO,
  InstantTransferSettleDialogDTO,
  instantTransferSettleDialogSchema,
  SuperPayInstantTransferConfirmDTO,
  superPayInstantTransferConfirmSchema,
  SuperPayInstantTransferDialogDTO,
  superPayInstantTransferDialogSchema,
  SuperPayInvitesShippersDTO,
  superPayInvitesShippersSchema,
} from 'shared/modules/superpay/SuperPayDTO';

export function useSuperPayInvitesShippers() {
  return useAPIQuery(
    ['superpay-invites'],
    () =>
      requestCarrierAPI<APIResponse<SuperPayInvitesShippersDTO>>(
        'GET /internal/web/payments/superpay-invites/',
      ),
    { schema: superPayInvitesShippersSchema, refetchOnWindowFocus: false },
  );
}

export function useSendSuperPayInvitesShippers() {
  return useAPIMutation((value: string[]) =>
    requestCarrierAPI('POST /internal/web/payments/superpay-invites/', {
      json: { shipper_guids: value },
    }),
  );
}

interface InstantTransferInformationProps {
  loadGUID?: string;
  loadID?: string;
}

export function useInstantTransferInformation(
  { loadGUID = '', loadID = '' }: InstantTransferInformationProps,
  options?: APIQueryOptions<SuperPayInstantTransferDialogDTO>,
) {
  return useAPIQuery(
    ['superpay-instant-transfer', loadGUID, loadID],
    () => {
      return requestCarrierAPI<APIResponse<SuperPayInstantTransferDialogDTO>>([
        'GET /internal/web/payments/instant-transfer/{?load_guid,load_id}',
        { load_guid: loadGUID, load_id: loadID },
      ]);
    },
    { schema: superPayInstantTransferDialogSchema, ...options },
  );
}

export function useConfirmInstantTransfer(
  guid?: string,
  options?: APIMutationOptions<void, SuperPayInstantTransferConfirmDTO>,
): APIMutationResult<void, SuperPayInstantTransferConfirmDTO> {
  return useAPIMutation(
    () =>
      requestCarrierAPI<APIResponse<SuperPayInstantTransferConfirmDTO>>(
        'POST /internal/web/payments/instant-transfer/',
        { json: { load_guid: guid } },
      ).then((response) => ({
        ...response,
        data: superPayInstantTransferConfirmSchema.cast(response.data),
      })),
    options,
  );
}

export function useInstantTransferReminder(options?: APIMutationOptions) {
  return useAPIMutation(
    () =>
      requestCarrierAPI(
        'PATCH /internal/web/payments/instant-transfer-nudging/',
        {
          json: { instant_transfer_nudging_enabled: false },
        },
      ),
    options,
  );
}

export function useInstantTransferNudgingStatistics(
  options: UseAPIQueryOptions<InstantTransferStatisticsSchemaDTO>,
) {
  return useAPIQuery(
    ['superpay-instant-transfer-statistic'],
    () =>
      requestCarrierAPI<APIResponse<InstantTransferStatisticsSchemaDTO>>(
        'GET /internal/web/payments/instant-transfer-nudging/',
      ),
    {
      schema: instantTransferStatisticsSchema,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
}

export function useInstantTransferInvitation() {
  return useAPIMutation(() =>
    requestCarrierAPI(
      'PATCH /internal/web/payments/instant-transfer-nudging/',
      {
        json: { instant_transfer_invitation_enabled: false },
      },
    ),
  );
}

interface InstantTransferSettleInformationProps {
  loadGUID?: string;
  loadID?: string;
}

export function useInstantTransferSettleInformation(
  { loadGUID = '', loadID = '' }: InstantTransferSettleInformationProps,
  options?: APIQueryOptions<InstantTransferSettleDialogDTO>,
) {
  return useAPIQuery(
    ['superpay-instant-transfer-settle', loadGUID, loadID],
    () =>
      requestCarrierAPI<APIResponse<InstantTransferSettleDialogDTO>>([
        'GET /internal/web/payments/instant-transfer-settlement/{?load_guid,load_id}',
        { load_guid: loadGUID, load_id: loadID },
      ]),
    { schema: instantTransferSettleDialogSchema, ...options },
  );
}

export function useSettleInstantTransfer(
  options?: APIMutationOptions<string[], void>,
) {
  return useAPIMutation(
    (loadGUIDs: string[]) =>
      requestCarrierAPI(
        'POST /internal/web/payments/instant-transfer-settlement/',
        {
          json: { load_guids: loadGUIDs },
        },
      ),
    options,
  );
}

export function useInstantTransferMultipleSettleInformation(
  options?: APIQueryOptions<InstantTransferSettleDialogDTO>,
): APIQueryResult<InstantTransferSettleDialogDTO> {
  return useAPIQuery(
    ['superpay-instant-transfer-multiple-settle'],
    () =>
      requestCarrierAPI(
        'GET /internal/web/payments/instant-transfer-settlement/',
      ),
    { schema: instantTransferSettleDialogSchema, ...options },
  );
}
